import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0); // 0: Mobile, 1: OTP, 2: Password

  const submitMobile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { mobile });
      setStep(1);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }
    setStep(2);
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!password || password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: otp, password });
      toast.success("Password updated successfully");
      navigate("/login");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="font-serif text-3xl tracking-tight text-[#1A1A1A]">EventFlow</Link>
        <h1 className="font-serif text-4xl tracking-tighter mt-8">Reset your password</h1>
        <p className="text-sm text-zinc-600 mt-2">We'll send a WhatsApp reset message to your mobile number.</p>

        {step === 0 && (
          <form onSubmit={submitMobile} className="mt-8 space-y-4" data-testid="forgot-form">
            <div>
              <Label htmlFor="mobile">Mobile number</Label>
              <Input 
                id="mobile" 
                required 
                inputMode="numeric" 
                maxLength={10} 
                value={mobile} 
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))} 
                className="mt-1.5 h-11" 
                placeholder="10-digit mobile number"
                data-testid="forgot-mobile-input" 
              />
            </div>
            <Button 
              type="submit" 
              disabled={loading || mobile.length !== 10} 
              className="w-full h-11 rounded-full bg-[#1A1A1A] text-white hover:bg-black" 
              data-testid="forgot-submit-btn"
            >
              {loading ? "Sending…" : "Send reset code"}
            </Button>
          </form>
        )}

        {step === 1 && (
          <>
            <div className="mt-8 p-5 border border-green-200 rounded-md bg-green-50" data-testid="forgot-sent-msg">
              <div className="text-green-900 font-medium">Check your WhatsApp</div>
              <div className="text-sm text-green-800 mt-1">We've sent a 6-digit code to +91{mobile}. Use it to reset your password. Valid for 1 hour.</div>
            </div>
            
            <form onSubmit={submitOTP} className="mt-8 space-y-4">
              <div>
                <Label htmlFor="otp">Reset code</Label>
                <Input 
                  id="otp" 
                  required 
                  inputMode="numeric" 
                  maxLength={6} 
                  value={otp} 
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} 
                  className="mt-1.5 h-11 text-center text-2xl tracking-widest" 
                  placeholder="000000"
                  data-testid="reset-otp-input"
                />
                <div className="text-xs text-zinc-500 mt-1">Enter the 6-digit code</div>
              </div>
              <Button 
                type="submit" 
                disabled={loading || otp.length !== 6} 
                className="w-full h-11 rounded-full bg-[#D85C38] hover:bg-[#B84A2A] text-white"
                data-testid="reset-otp-submit-btn"
              >
                Verify code
              </Button>
              <button 
                type="button"
                onClick={() => {
                  setStep(0);
                  setOtp("");
                }}
                className="w-full text-sm text-zinc-600 hover:text-[#1A1A1A]"
                data-testid="reset-back-mobile-btn"
              >
                ← Use different mobile
              </button>
            </form>
          </>
        )}

        {step === 2 && (
          <form onSubmit={submitPassword} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="password">New password</Label>
              <Input 
                id="password" 
                required 
                type="password" 
                minLength={8} 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                className="mt-1.5 h-11"
                placeholder="Minimum 8 characters"
                data-testid="reset-password-input"
              />
              <div className="text-xs text-zinc-500 mt-1">Minimum 8 characters</div>
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm password</Label>
              <Input 
                id="confirm-password" 
                required 
                type="password" 
                minLength={8} 
                value={confirmPassword} 
                onChange={(e) => setConfirmPassword(e.target.value)} 
                className="mt-1.5 h-11"
                placeholder="Re-enter password"
                data-testid="reset-confirm-password-input"
              />
            </div>
            <Button 
              type="submit" 
              disabled={loading} 
              className="w-full h-11 rounded-full bg-[#1A1A1A] text-white hover:bg-black"
              data-testid="reset-password-submit-btn"
            >
              {loading ? "Updating…" : "Update password"}
            </Button>
            <button 
              type="button"
              onClick={() => setStep(1)}
              className="w-full text-sm text-zinc-600 hover:text-[#1A1A1A]"
              data-testid="reset-back-otp-btn"
            >
              ← Back to enter code
            </button>
          </form>
        )}

        <Link to="/login" className="block mt-8 text-sm text-zinc-600 hover:text-[#1A1A1A]">← Back to sign in</Link>
      </div>
    </div>
  );
}
